// Mirrors the UrlMapDetails shape emitted by the gcp-asset-ingestor backend
// (plugins/gcp-asset-ingestor-backend/src/transform/details/types.ts). Kept as
// a local copy because the app package does not depend on the backend plugin.

interface RouteHeaderMatch {
  name: string;
  op: string;
  value?: string;
  invert?: boolean;
}
interface RouteQueryMatch {
  name: string;
  op: string;
  value?: string;
}
export interface RouteMatch {
  prefix?: string;
  fullPath?: string;
  regex?: string;
  ignoreCase?: boolean;
  headers?: RouteHeaderMatch[];
  queryParams?: RouteQueryMatch[];
}
export type RouteTarget =
  | { kind: 'service'; service: string }
  | { kind: 'weighted'; backends: Array<{ service: string; weight?: number }> }
  | {
      kind: 'redirect';
      hostRedirect?: string;
      pathRedirect?: string;
      prefixRedirect?: string;
      responseCode?: string;
      httpsRedirect?: boolean;
      stripQuery?: boolean;
    };
interface UrlMapRoute {
  priority?: number;
  matches: RouteMatch[];
  target?: RouteTarget;
  rewrite?: { pathPrefix?: string; host?: string };
  mirror?: string;
}
interface UrlMapPathMatcher {
  name: string;
  hosts: string[];
  default?: RouteTarget;
  routes: UrlMapRoute[];
}
export interface UrlMapDetails {
  default?: RouteTarget;
  pathMatchers: UrlMapPathMatcher[];
}

const MAX_LABEL = 36;

function sanitizeId(s: string): string {
  return s.replace(/[^a-zA-Z0-9]/g, '_');
}

/** Mermaid-safe label: drop quotes/pipes/newlines that break the parser. */
function esc(s: string): string {
  return s
    .replace(/["|]/g, "'")
    .replace(/[\r\n]+/g, ' ')
    .trim();
}

function truncate(s: string): string {
  return s.length > MAX_LABEL ? `${s.slice(0, MAX_LABEL - 1)}…` : s;
}

const HEADER_OP_SYMBOL: Record<string, string> = {
  prefix: '^=',
  suffix: '=$',
  regex: '~',
  range: '∈',
  exact: '=',
};

function headerLabel(h: RouteHeaderMatch): string {
  const not = h.invert ? '!' : '';
  if (h.op === 'present') return `${h.name}${not}(present)`;
  const sym = HEADER_OP_SYMBOL[h.op] ?? '=';
  return `${h.name}${not}${sym}${h.value ?? ''}`;
}

function pathLabel(m: RouteMatch): string {
  if (m.prefix !== undefined) return `${m.prefix}*`;
  if (m.fullPath !== undefined) return m.fullPath;
  if (m.regex !== undefined) return `~${m.regex}`;
  return '';
}

function matchLabel(m: RouteMatch): string {
  const parts: string[] = [];
  const path = pathLabel(m);
  if (path) parts.push(path);
  (m.headers ?? []).forEach(h => parts.push(headerLabel(h)));
  (m.queryParams ?? []).forEach(q =>
    parts.push(`?${q.name}${q.value ? `=${q.value}` : ''}`),
  );
  return parts.join(' ');
}

/** Summarize a route's match rules into one short edge label. */
export function summarizeMatches(matches: RouteMatch[]): string {
  if (!matches.length) return 'any';
  const label = matches.map(matchLabel).filter(Boolean).join(' | ') || 'any';
  return truncate(label);
}

function redirectLabel(t: Extract<RouteTarget, { kind: 'redirect' }>): string {
  const host = t.hostRedirect ?? '';
  const path = t.pathRedirect ?? t.prefixRedirect ?? '';
  return truncate(`↪ ${host}${path}` || '↪ redirect');
}

export type UpstreamNodeKind =
  'cname' | 'dnsRecord' | 'forwardingRule' | 'proxy';

export interface UpstreamNode {
  /** Unique id for this node (e.g. a stringified entity ref) — sanitized internally. */
  ref: string;
  label: string;
  kind: UpstreamNodeKind;
}

interface UpstreamEdge {
  /** ref of the node that depends on `to` (e.g. a CNAME depending on an A record). */
  from: string;
  to: string;
}

/** The chain of things upstream of a UrlMap: CNAME -> A/AAAA record -> forwarding rule -> proxy. */
export interface UpstreamChart {
  nodes: UpstreamNode[];
  edges: UpstreamEdge[];
  /** refs of the proxy nodes — these feed directly into the UrlMap itself. */
  proxyRefs: string[];
}

const UPSTREAM_SHAPE: Record<UpstreamNodeKind, [string, string]> = {
  cname: ['([', '])'],
  dnsRecord: ['(', ')'],
  forwardingRule: ['[', ']'],
  proxy: ['[', ']'],
};

const UPSTREAM_CLASS: Record<UpstreamNodeKind, string> = {
  cname: 'cname',
  dnsRecord: 'dnsrecord',
  forwardingRule: 'fr',
  proxy: 'proxy',
};

/**
 * Build a Mermaid `flowchart LR` describing a UrlMap's routing: one node per
 * host group (path matcher), edges labelled with the match predicate, leading
 * to backend / redirect nodes. When `upstream` is given, also prepends the
 * CNAME -> A/AAAA record -> forwarding rule -> proxy chain feeding into this
 * UrlMap, wiring each proxy directly to the host groups it routes to (the
 * UrlMap itself is just the routing config, not a node worth drawing).
 * Returns undefined when there is nothing to draw at all.
 */
export function buildRoutingChart(
  details: UrlMapDetails,
  upstream?: UpstreamChart,
): string | undefined {
  const hasMatchers = (details.pathMatchers ?? []).some(
    pm => pm.routes.length > 0 || pm.default,
  );
  const hasUpstream = !!upstream && upstream.nodes.length > 0;
  if (!hasMatchers && !details.default && !hasUpstream) return undefined;

  const lines: string[] = ['flowchart LR'];
  const backendNodes = new Map<string, string>(); // service name -> node id
  let redirectSeq = 0;
  const pathMatcherIds: string[] = [];

  const backendNode = (service: string): string => {
    const existing = backendNodes.get(service);
    if (existing) return existing;
    const nodeId = `bk_${sanitizeId(service)}`;
    backendNodes.set(service, nodeId);
    lines.push(`  ${nodeId}["${esc(service)}"]:::backend`);
    return nodeId;
  };
  const redirectNode = (t: Extract<RouteTarget, { kind: 'redirect' }>) => {
    const nodeId = `rd_${(redirectSeq += 1)}`;
    lines.push(`  ${nodeId}("${esc(redirectLabel(t))}"):::redirect`);
    return nodeId;
  };

  const edge = (from: string, label: string, to: string) =>
    lines.push(`  ${from} -->|"${esc(label)}"| ${to}`);

  const linkTarget = (from: string, label: string, target?: RouteTarget) => {
    if (!target) return;
    if (target.kind === 'service') {
      edge(from, label, backendNode(target.service));
    } else if (target.kind === 'weighted') {
      target.backends.forEach(b => {
        const w = typeof b.weight === 'number' ? ` ${b.weight}%` : '';
        edge(from, `${label}${w}`.trim(), backendNode(b.service));
      });
    } else {
      edge(from, label, redirectNode(target));
    }
  };

  const upstreamNodeId = new Map<string, string>(); // ref -> node id
  if (hasUpstream) {
    upstream!.nodes.forEach(n => {
      const nodeId = `up_${sanitizeId(n.ref)}`;
      upstreamNodeId.set(n.ref, nodeId);
      const [open, close] = UPSTREAM_SHAPE[n.kind];
      lines.push(
        `  ${nodeId}${open}"${esc(n.label)}"${close}:::${UPSTREAM_CLASS[n.kind]}`,
      );
    });
    upstream!.edges.forEach(e => {
      const from = upstreamNodeId.get(e.from);
      const to = upstreamNodeId.get(e.to);
      if (from && to) lines.push(`  ${from} --> ${to}`);
    });
  }

  (details.pathMatchers ?? []).forEach((pm, i) => {
    const hostLabel =
      pm.hosts.length > 0
        ? `${pm.hosts[0]}${pm.hosts.length > 1 ? ` (+${pm.hosts.length - 1})` : ''}`
        : pm.name;
    const hostId = `pm${i}`;
    pathMatcherIds.push(hostId);
    lines.push(`  ${hostId}{{"${esc(hostLabel)}"}}:::host`);

    pm.routes.forEach(route => {
      const rewrite = route.rewrite?.pathPrefix ? ' ↻' : '';
      linkTarget(
        hostId,
        `${summarizeMatches(route.matches)}${rewrite}`,
        route.target,
      );
      if (route.mirror) {
        lines.push(`  ${hostId} -. mirror .-> ${backendNode(route.mirror)}`);
      }
    });
    linkTarget(hostId, 'default', pm.default);
  });

  if (details.default) {
    lines.push('  root{{"unmatched"}}:::host');
    pathMatcherIds.push('root');
    linkTarget('root', 'default', details.default);
  }

  if (hasUpstream) {
    const proxyNodeIds = upstream!.proxyRefs
      .map(ref => upstreamNodeId.get(ref))
      .filter((id): id is string => !!id);
    proxyNodeIds.forEach(proxyId =>
      pathMatcherIds.forEach(id => lines.push(`  ${proxyId} --> ${id}`)),
    );
  }

  lines.push('  classDef host fill:#e3f2fd,stroke:#1565c0,color:#0d47a1;');
  lines.push('  classDef backend fill:#e8f5e9,stroke:#2e7d32,color:#1b5e20;');
  lines.push('  classDef redirect fill:#fce4ec,stroke:#ad1457,color:#880e4f;');
  if (hasUpstream) {
    lines.push('  classDef cname fill:#fff3e0,stroke:#e65100,color:#bf360c;');
    lines.push(
      '  classDef dnsrecord fill:#fff8e1,stroke:#f9a825,color:#7f5f01;',
    );
    lines.push('  classDef fr fill:#ede7f6,stroke:#4527a0,color:#311b92;');
    lines.push('  classDef proxy fill:#e0f7fa,stroke:#00838f,color:#004d51;');
  }

  return lines.join('\n');
}
